<template>
  <div class="chatbot-wrapper">
    <div class="chatbot">
      <div v-if="messages.length" ref="content" class="scrollable-content">
        <div ref="list" class="message-list">
          <Node v-for="(conv, index) in messages" :key="index">
            <ErrorMessage v-if="isErrorMessage(conv.messageType)" :message="conv.message" />
            <ContextMessage v-else-if="isContextMessage(conv.messageType)" :message="conv" />
            <Message v-else :message="conv" :is-continuation="isContinuation(index)" :reply="replies[index]" />
          </Node>
        </div>
        <div :style="{ height: `${spacerHeight}px` }" />
      </div>
      <div v-else class="no-messages-title">
        <div class="icon">
          <img :src="productIcon" alt="product" />
        </div>
        <div>{{ t('conversation.whatCanIHelp') }}</div>
      </div>

      <!-- Scroll to bottom button -->
      <transition name="fade">
        <BbBtn
          v-if="showScrollButton"
          class="scroll-to-bottom-btn"
          type="icon"
          icon="chevron-down"
          icon-size="medium"
          :aria-label="t('conversation.scrollToBottom')"
          @click="scrollToBottom"
        />
      </transition>
    </div>
    <MessageInput @onSubmit="onSubmitValue" />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import productIcon from '~/assets/svg/product-icon.svg';

import { DEFAULT_COLLECTION, messageType, queryType, Role } from '~/constants/conversationModels';
import { collectReplySteps, groupMessageChunks, isStepGroup } from '~/utils/message';

import Message from '~/components/conversation/chatMessage/Message';
import Node from '~/components/conversation/chatMessage/Node';
import MessageInput from '~/components/conversation/MessageInput';
import useConversations from '~/composables/useConversations';
import ContextMessage from '~/components/conversation/chatMessage/ContextMessage';
import ErrorMessage from '~/components/conversation/chatMessage/ErrorMessage';
import { useTopAnchoredScroll } from '~/composables/useTopAnchoredScroll';

export default {
  name: 'Conversation',
  components: {
    ContextMessage,
    MessageInput,
    Message,
    Node,
    ErrorMessage,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const { createNewConversation, currentConversation, publishToConversation, setIsNewConversation } =
      useConversations();
    const messages = computed(() => currentConversation.value.messages);
    const { content, list, spacerHeight, showScrollButton, scrollToBottom, prepareForNewMessage, resetScrollState } =
      useTopAnchoredScroll(messages);

    const conversationId = computed(() => currentConversation.value.conversationId);

    const conversationStack = ref([]);

    const isContextMessage = (type) => type === messageType.context;
    const isErrorMessage = (type) => type === messageType.error;

    const isAssistantReply = (message) =>
      Boolean(message) &&
      message.role !== Role.user &&
      !isContextMessage(message.messageType) &&
      !isErrorMessage(message.messageType);

    // An assistant message straight after another one (e.g. the reply streamed after approving a tool
    // call is a new turn) reads as part of the same reply, so it doesn't repeat the avatar.
    const isContinuation = (index) =>
      index > 0 && isAssistantReply(messages.value[index]) && isAssistantReply(messages.value[index - 1]);

    // One reply is a run of consecutive assistant messages. All its tool calls and answered approvals show
    // as steps of one block, in the first of its messages that has any; indexed like `messages`.
    const replies = computed(() => {
      const result = [];
      let run = [];

      const closeRun = () => {
        const groupsPerMessage = run.map((index) => groupMessageChunks(messages.value[index].chunks || []));
        const replySteps = collectReplySteps(groupsPerMessage);
        const owner = run[groupsPerMessage.findIndex((groups) => groups.some(isStepGroup))];
        const isTyping = run.some((index) => messages.value[index].isTyping);
        run.forEach((index) => {
          result[index] = { ownsSteps: index === owner, ...replySteps, isTyping };
        });
        run = [];
      };

      messages.value.forEach((message, index) => {
        if (isAssistantReply(message)) {
          run.push(index);
        } else if (run.length) {
          closeRun();
        }
      });
      if (run.length) closeRun();

      return result;
    });

    const shouldShowScopeMessage = (message, index) => {
      if (index === 0 && message.collectionId === DEFAULT_COLLECTION.collectionId) {
        return false;
      }
      return index === 0 || messages.value[index - 1].collectionId !== message.collectionId;
    };

    const onSubmitValue = async (value) => {
      if (!route.params.conversationId) {
        setIsNewConversation(true);
        await createNewConversation();
      }
      prepareForNewMessage();
      await publishToConversation(conversationId.value, queryType.search, value);
    };

    watch(
      () => route.params.conversationId,
      () => resetScrollState(),
    );

    return {
      t,
      content,
      list,
      spacerHeight,
      conversationStack,
      isContextMessage,
      isContinuation,
      isErrorMessage,
      messages,
      replies,
      onSubmitValue,
      shouldShowScopeMessage,
      productIcon,
      showScrollButton,
      scrollToBottom,
    };
  },
};
</script>

<style lang="scss" scoped>
.chatbot-wrapper {
  --primary-color: #{$bb-primary-1};
  --primary-dimmed-color: #{$bb-primary-1-dimmed};

  @include bb-base-font-family;
  @include bb-regular-text-styles('md');

  @include media-breakpoint-up('sm') {
    // padding: 0 156px $bb-spacing-4 0;
    // padding: 0 $bb-spacing-8 0 $bb-spacing-8;
  }

  height: 100%;

  color: $bb-neutral-1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: antialiased;
  font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%; /* Prevent adjustments of font size after orientation changes in iOS */

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-5;
  width: 100%;
  position: relative;
  flex: 1;
  max-width: 1024px;
  margin: auto;
  padding: $bb-spacing-4;
  min-width: 0;

  .chatbot {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    position: relative;

    .no-messages-title {
      font-size: 38px !important;
      margin: auto;
      display: flex;
      align-items: center;
      gap: $bb-spacing-6;

      .icon {
        background: #fdb91a;
        border-radius: 100px;
        padding: $bb-spacing-2;
      }
    }
  }

  .scrollable-content {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-right: $bb-spacing-4;
  }

  .message-list {
    display: flow-root;
  }

  .scroll-to-bottom-btn {
    position: absolute;
    bottom: $bb-spacing-4;
    left: 50%;
    transform: translateX(-50%);
    width: 40px !important;
    height: 40px !important;
    border-radius: 50%;
    background-color: $bb-neutral-7;
    border: 1px solid $bb-neutral-5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
      background-color: $bb-neutral-0 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateX(-50%) scale(0.95);
    }

    :deep(svg),
    :deep(i) {
      color: $bb-neutral-2;
    }
  }

  // Fade transition for the button
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
